import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import { editorialImageSrcSet } from "../utils/images";
import "../css/about.scss";

function About() {
  const { brandName, description } = siteConfig;

  return (
    <main className="content-page about-page">
      <header className="page-hero page-hero--editorial">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Our story</p>
          <h1>Italian food, chosen at the source.</h1>
          <p className="page-hero__lede">
            {brandName} brings remarkable pantry staples from independent
            Italian producers to curious home cooks.
          </p>
        </div>
      </header>

      <section className="page-section page-shell editorial-split">
        <div className="editorial-split__media">
          <img
            src="/main/about-kitchen.jpg"
            srcSet={editorialImageSrcSet("/main/about-kitchen.jpg")}
            sizes="(max-width: 760px) 100vw, 50vw"
            alt="Two food makers sorting tomatoes and wrapping pantry goods in a sunlit workshop"
            width="1800"
            height="1200"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="editorial-split__copy prose">
          <p className="eyebrow">A pantry with a point of view</p>
          <h2>Food tastes better when you know its story</h2>
          <p>
            We started with a simple conviction: the ingredients used every day
            deserve the same care as the dishes saved for special occasions.
            That means olive oil pressed from a named harvest, pasta shaped for
            the sauce it will carry, and preserves that still taste of
            peak-season fruit.
          </p>
          <p>
            Our assortment is intentionally edited. We seek out foods with a
            clear sense of place, then learn how they are grown, made, packed,
            and best enjoyed before they earn a place on our shelves.
          </p>
          {description && <p className="about-page__mission">{description}</p>}
        </div>
      </section>

      <section className="page-section page-section--tinted">
        <div className="page-shell">
          <div className="section-heading section-heading--centered">
            <p className="eyebrow">What guides us</p>
            <h2>Good food begins with good relationships</h2>
          </div>
          <div className="value-grid">
            <article className="value-card">
              <span className="value-card__number" aria-hidden="true">
                01
              </span>
              <h3>Know the producer</h3>
              <p>
                We favor small and family-run makers whose knowledge has been
                built over seasons, not shortcuts.
              </p>
            </article>
            <article className="value-card">
              <span className="value-card__number" aria-hidden="true">
                02
              </span>
              <h3>Respect the ingredient</h3>
              <p>
                Flavor, provenance, and honest methods matter more to us than an
                endless catalog or passing trends.
              </p>
            </article>
            <article className="value-card">
              <span className="value-card__number" aria-hidden="true">
                03
              </span>
              <h3>Share what we learn</h3>
              <p>
                Clear notes on origin, craft, and serving turn every jar and
                package into a useful part of your kitchen.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="page-section page-shell sourcing-section">
        <div className="section-heading">
          <p className="eyebrow">How we source</p>
          <h2>From a producer’s hands to your table</h2>
        </div>
        <div className="sourcing-section__content prose">
          <p>
            We begin with taste, then look deeper. We ask who grew the raw
            ingredients, where they came from, which techniques shape the
            finished food, and whether the producer can maintain that quality
            from one batch to the next.
          </p>
          <ul className="check-list">
            <li>Distinctive flavor and a transparent place of origin</li>
            <li>Traditional craft paired with exacting modern standards</li>
            <li>Responsible production and packaging wherever practical</li>
            <li>Storage and shipping plans that protect quality in transit</li>
          </ul>
          <p>
            The result is not a museum of rare ingredients. It is a working
            pantry: food made to be opened, poured, shared, and reordered.
          </p>
        </div>
      </section>

      <section className="page-cta">
        <div className="page-shell page-cta__inner">
          <div>
            <p className="eyebrow">Bring Italy to the table</p>
            <h2>Find your next pantry favorite.</h2>
          </div>
          <div className="page-cta__actions">
            <Link className="button button--primary" to="/shop">
              Shop the collection
            </Link>
            <Link className="text-link" to="/contact">
              Talk with us <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default About;
